import { useSetAtom, useAtomValue } from "jotai";
import { useCallback, useRef } from "react";
import { fetchTasksAtom } from "../../store/actions.js";
import {
  activeSlateAtom,
  showCompletedAtom,
  completedFilterAtom,
} from "../../store/atoms/index.js";
import { useErrorHandler } from "./useErrorHandler.js";
import { useLoading } from "./useLoading.js";

export const useFetchTasks = () => {
  const fetchTasks = useSetAtom(fetchTasksAtom);
  const activeSlate = useAtomValue(activeSlateAtom);
  const showCompleted = useAtomValue(showCompletedAtom);
  const completedFilter = useAtomValue(completedFilterAtom);
  const { handleTaskError } = useErrorHandler();
  const { withTaskLoading } = useLoading();

  // Prevent duplicate requests
  const isRequestInProgress = useRef(false);
  const lastRequestParams = useRef(null);

  const handleFetchTasks = useCallback(
    async (force = false) => {
      const currentParams = `${activeSlate}-${showCompleted}-${completedFilter}`;

      // Skip if same request is already in progress or same params (unless forced)
      if (
        !force &&
        (isRequestInProgress.current ||
          lastRequestParams.current === currentParams)
      ) {
        return;
      }

      isRequestInProgress.current = true;
      lastRequestParams.current = currentParams;

      try {
        await withTaskLoading(fetchTasks)();
      } catch (error) {
        if (error.message?.includes("Authentication required")) {
          throw error;
        }
        handleTaskError(error, true);
      } finally {
        isRequestInProgress.current = false;
      }
    },
    [
      fetchTasks,
      activeSlate,
      showCompleted,
      completedFilter,
      handleTaskError,
      withTaskLoading,
    ]
  );

  // Optimized dependencies - only the values that affect the API call
  const dependencies = [activeSlate, showCompleted, completedFilter];

  return {
    fetchTasks: handleFetchTasks,
    dependencies,
    isRequestInProgress: isRequestInProgress.current,
  };
};
